import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CampaignInsightsService, CampaignNextSend } from './campaign-insights.service';
import { formatCountdown, formatWhen } from '../../../../utils/sms';
import {
  browserTimeZone,
  canStillSend,
  zoneLabel,
} from '../../../pages/organization/campaigns/campaign-list/campaign-facts';

/**
 * When the campaign sends next: a countdown over the time, for campaigns
 * that can still send. Finished campaigns show a dash without asking.
 */
@Component({
  selector: 'bk-campaign-next-send',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (state) {
      @case ("loading") {
        <span class="n">
          <span class="bk-sr-only">Loading next send</span>
          <span class="bk-skel n__skel-line"></span>
          <span class="bk-skel n__skel-sub"></span>
        </span>
      }
      @case ("ready") {
        @if (next.at) {
          <span class="n" [attr.title]="title">
            <span class="n__countdown" aria-hidden="true">{{ countdown }}</span>
            <span class="n__when" aria-hidden="true">{{ when }}</span>
            <span class="bk-sr-only">{{ countdown }}: {{ title }}</span>
          </span>
        } @else {
          <span class="d__none" title="No sends are scheduled after now">Nothing queued</span>
        }
      }
      @case ("error") {
        <span class="d__none" title="The schedule could not be loaded">—</span>
      }
      @default {
        <span class="d__none">—</span>
      }
    }
  `,
  styleUrl: './campaign-cells.css',
})
export class CampaignNextSendComponent implements OnChanges, OnDestroy {
  private insights = inject(CampaignInsightsService);
  private cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  @Input({ required: true }) campaign: any;
  /** Ticks from the table so countdowns stay current without their own timers. */
  @Input() now: Date = new Date();

  state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  next: CampaignNextSend = { at: null, upcoming: 0, timeZone: null };

  get countdown(): string {
    return formatCountdown(this.next.at, this.now);
  }

  get when(): string {
    return formatWhen(this.next.at, this.now);
  }

  get title(): string {
    const more = this.next.upcoming > 1 ? `, then ${this.next.upcoming - 1} more` : '';
    const zone = this.next.timeZone && this.next.timeZone !== browserTimeZone() ? ` (scheduled in ${zoneLabel(this.next.timeZone)})` : '';
    return `Next send ${this.when}${zone}${more}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['campaign']) {
      // Only the clock moved.
      this.cdr.markForCheck();
      return;
    }

    this.sub?.unsubscribe();
    if (!this.campaign || !canStillSend(this.campaign.status)) {
      this.state = 'idle';
      return;
    }

    this.state = 'loading';
    this.sub = this.insights.nextSend(this.campaign).subscribe({
      next: (next) => {
        this.next = next;
        this.state = 'ready';
        this.cdr.markForCheck();
      },
      error: () => {
        this.state = 'error';
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
